// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// Store
import { useAppState, useAppDispatch } from '@/store';
import {
  changeOrderRegion,
  changeOrderStatus,
  getManagersList,
  getOrderDetails,
  ordersSelector,
  resetOrderInfoState,
} from '../../feature';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';
import { useAutocompleteRegions } from '@/modules/Regions/hooks';
import { selectAdminProfile } from '@/modules/AdminProfile';

// Models
import { IApiError } from '@/models/apiError.model';
import { Roles } from '@/models/admins.model';
import { OrdersPaymentStatus, OrdersStatus, OrderCancellationReason } from '@/models/orders.model';

// Components
import {
  StyledPageWrapper,
  StyledHeaderWrapper,
  StyledHeaderButtonsWrapper,
  StyledHeaderLeftBlock,
  StyledHeaderRightBlock,
  StyledTextButton,
  StyledCancelButton,
  StyledCancelIconWrapper,
  StyledPageTitle,
  StyledContentWrapper,
} from './OrderDetailsMainContainerStyles';
import { ChevronIcon, CloseIcon } from '@/icons';
import { OrderProductsContainer, OrderAdditionalInfoContainer } from '../../containers';
import { CancelOrderModal, ChangeStatusModal, OrderStatusBadge } from '../../components';
import { showErrorToast } from '@/components';
import { Button, Tooltip } from '@/ui';

// Helpers
import { getHeaderButtonConfig, getRegionSelectConfig } from '../../helpers';
import { useModal } from '@/hooks';

type TStatusModalConfig = {
  nextStatus: OrdersStatus;
  requiresRegionSelection: boolean;
};

const OrdersMainContainer = () => {
  const { t } = useTranslation(['orders', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { order, isLoading, managers } = useAppState(ordersSelector);
  const profile = useAppState(selectAdminProfile);
  const { canManage } = usePermissions();
  const canManageOrders = canManage(Resource.ORDER);
  const { onSearch: onSearchRegions, regionsOptions } = useAutocompleteRegions();
  const {
    isOpened: isOpenCancelOrderModal,
    openModal: openCancelOrderModal,
    closeModal: closeCancelOrderModal,
  } = useModal();
  const { isOpened, openModal, closeModal } = useModal();
  const { isOpened: isOpenedRollback, openModal: openRollbackModal, closeModal: closeRollbackModal } = useModal();
  const [statusModalConfig, setStatusModalConfig] = useState<TStatusModalConfig | null>(null);
  const headerButtonConfig = getHeaderButtonConfig(order?.status, order?.delivery?.delivery, profile?.role);
  const isCompletedButtonDisabled =
    headerButtonConfig?.nextStatus === OrdersStatus.COMPLETED && order?.payment_status !== OrdersPaymentStatus.PAID;
  const showRollbackButton =
    (order?.status === OrdersStatus.ACCEPTED || order?.status === OrdersStatus.READY_FOR_TTN) &&
    order?.payment_status !== OrdersPaymentStatus.PAID &&
    order?.payment_status !== OrdersPaymentStatus.PREPAID;
  const showReserveButton = order?.status === OrdersStatus.PROCESSING && profile?.role !== Roles.LOGISTICIAN;
  const hasOrderRegion = Boolean(order?.region?.id || order?.region_id);
  const managerRegionId = managers.find((m) => m.id === order?.manager?.id)?.region_id ?? null;
  const showHeaderActions =
    canManageOrders && !!order && order.status !== OrdersStatus.COMPLETED && order.status !== OrdersStatus.CANCELLED;

  const goBack = () => {
    navigate(-1);
  };

  const getErrorMessage = (error: unknown) => {
    const apiError = error as IApiError;
    return apiError?.message || t('order_status_change_error');
  };

  const handleDirectStatusChange = async (status: OrdersStatus) => {
    try {
      await dispatch(changeOrderStatus({ order_id: id as string, status })).unwrap();
      await dispatch(getOrderDetails(id as string)).unwrap();
    } catch (error) {
      showErrorToast({ message: getErrorMessage(error) });
    }
  };

  const openStatusChangeModal = () => {
    if (!headerButtonConfig) {
      return;
    }

    // Ready-for-TTN is a hand-off to logistics — no confirmation modal.
    if (headerButtonConfig.nextStatus === OrdersStatus.READY_FOR_TTN) {
      void handleDirectStatusChange(OrdersStatus.READY_FOR_TTN);
      return;
    }

    setStatusModalConfig({
      nextStatus: headerButtonConfig.nextStatus,
      requiresRegionSelection: headerButtonConfig.nextStatus === OrdersStatus.ACCEPTED && !hasOrderRegion,
    });
    openModal();
  };

  const closeStatusChangeModal = () => {
    closeModal();
  };

  const handleChangeOrderStatus = async (regionId?: string) => {
    try {
      if (!statusModalConfig) {
        return;
      }

      if (statusModalConfig.requiresRegionSelection) {
        if (!regionId) {
          showErrorToast({ message: t('order_confirm_region_required') });
          return;
        }

        await dispatch(changeOrderRegion({ order_id: id as string, region_id: regionId })).unwrap();
      }

      await dispatch(changeOrderStatus({ order_id: id as string, status: statusModalConfig.nextStatus })).unwrap();
      closeStatusChangeModal();
      await dispatch(getOrderDetails(id as string)).unwrap();
    } catch (error) {
      showErrorToast({ message: getErrorMessage(error) });
    }
  };

  const handleRollbackStatus = async (_regionId?: string) => {
    try {
      await dispatch(changeOrderStatus({ order_id: id as string, status: OrdersStatus.PROCESSING })).unwrap();
      await dispatch(getOrderDetails(id as string)).unwrap();
      closeRollbackModal();
    } catch (error) {
      showErrorToast({ message: getErrorMessage(error) });
    }
  };

  const handleCancelOrder = async (reason: OrderCancellationReason, reasonText?: string) => {
    try {
      await dispatch(
        changeOrderStatus({
          order_id: id as string,
          status: OrdersStatus.CANCELLED,
          cancellation_reason: reason,
          cancellation_reason_text: reasonText,
        }),
      ).unwrap();
      await dispatch(getOrderDetails(id as string)).unwrap();
      closeCancelOrderModal();
    } catch (error) {
      showErrorToast({ message: getErrorMessage(error) });
    }
  };

  const handleReserveOrder = async () => {
    try {
      await dispatch(changeOrderStatus({ order_id: id as string, status: OrdersStatus.RESERVE })).unwrap();
      await dispatch(getOrderDetails(id as string)).unwrap();
    } catch (error) {
      showErrorToast({ message: getErrorMessage(error) });
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getOrderDetails(id));
    }
  }, [id]);

  useEffect(() => {
    if (canManageOrders && profile?.role !== Roles.LOGISTICIAN && managers.length === 0) {
      dispatch(getManagersList());
    }
  }, [canManageOrders, profile?.role]);

  useEffect(() => {
    return () => {
      dispatch(resetOrderInfoState());
    };
  }, []);

  return (
    <StyledPageWrapper>
      <StyledTextButton onClick={goBack}>
        <ChevronIcon />
        {t('button_common_back', { ns: 'common' })}
      </StyledTextButton>
      <StyledHeaderWrapper>
        <StyledHeaderLeftBlock>
          <StyledPageTitle>{`${t('order_products_list_order_num')}${order?.number || 0}`}</StyledPageTitle>
          {order && <OrderStatusBadge t={t} record={order} />}
        </StyledHeaderLeftBlock>
        <StyledHeaderRightBlock>
          {showHeaderActions && (
            <StyledHeaderButtonsWrapper>
              <StyledCancelButton onClick={openCancelOrderModal}>
                <StyledCancelIconWrapper>
                  <CloseIcon />
                </StyledCancelIconWrapper>
                {t('order_details_cancel_order_btn')}
              </StyledCancelButton>
              {showReserveButton && (
                <Button type="default" onClick={handleReserveOrder}>
                  {t('order_details_reserve_order_btn')}
                </Button>
              )}
              {showRollbackButton && (
                <Button type="default" onClick={openRollbackModal}>
                  {t('order_details_rollback_to_processing_btn')}
                </Button>
              )}
              {headerButtonConfig && (
                <Tooltip
                  title={isCompletedButtonDisabled ? t('order_details_completed_btn_disabled_tooltip') : undefined}
                >
                  <Button type="primary" onClick={openStatusChangeModal} disabled={isCompletedButtonDisabled}>
                    {t(headerButtonConfig.label)}
                  </Button>
                </Tooltip>
              )}
            </StyledHeaderButtonsWrapper>
          )}
        </StyledHeaderRightBlock>
      </StyledHeaderWrapper>
      <StyledContentWrapper>
        <OrderProductsContainer />
        <OrderAdditionalInfoContainer />
      </StyledContentWrapper>
      {canManageOrders && (
        <CancelOrderModal
          isLoading={isLoading}
          isOpenCancelOrderModal={isOpenCancelOrderModal}
          closeCancelOrderModal={closeCancelOrderModal}
          handleCancelOrder={handleCancelOrder}
        />
      )}
      {canManageOrders && statusModalConfig && (
        <ChangeStatusModal
          isLoading={isLoading}
          isOpened={isOpened}
          closeCancelOrderModal={closeStatusChangeModal}
          handleChangeOrderStatus={handleChangeOrderStatus}
          nextStatus={statusModalConfig.nextStatus}
          requiresRegionSelection={statusModalConfig.requiresRegionSelection}
          regionOptions={getRegionSelectConfig(regionsOptions, order?.region) ?? []}
          onSearchRegions={onSearchRegions}
          defaultRegionId={managerRegionId}
        />
      )}
      {canManageOrders && showRollbackButton && (
        <ChangeStatusModal
          isLoading={isLoading}
          isOpened={isOpenedRollback}
          closeCancelOrderModal={closeRollbackModal}
          handleChangeOrderStatus={handleRollbackStatus}
          nextStatus={OrdersStatus.PROCESSING}
        />
      )}
    </StyledPageWrapper>
  );
};

export default OrdersMainContainer;
